import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Card from '../components/Card';
import Modal from '../components/Modal';
import PasswordStrength, { validatePassword } from '../components/PasswordStrength';
import { useAuthModal } from '../contexts/AuthModalContext';
import { login, register } from '../services/auth';
import './Landing.css';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const {
    showLoginModal,
    showRegisterModal,
    openLoginModal,
    openRegisterModal,
    closeModals
  } = useAuthModal();

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Register state
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const result = await login({ email: loginEmail, password: loginPassword });

      if (result.success) {
        closeModals();
        navigate('/dashboard');
      } else {
        setLoginError(result.message || 'Login failed');
      }
    } catch (err) {
      setLoginError('An unexpected error occurred');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError('');
    setRegisterSuccess('');

    if (registerPassword !== confirmPassword) {
      setRegisterError('Passwords do not match');
      return;
    }

    const validation = validatePassword(registerPassword);
    if (!validation.minLength || !validation.hasSymbol) {
      setRegisterError('Password does not meet security requirements');
      return;
    }

    setRegisterLoading(true);

    try {
      const result = await register({ email: registerEmail, password: registerPassword });

      if (result.success) {
        setRegisterSuccess(result.message || 'Registration successful! Your account is pending admin approval.');
        setTimeout(() => {
          closeModals();
          setRegisterSuccess('');
          setRegisterEmail('');
          setRegisterPassword('');
          setConfirmPassword('');
        }, 3000);
      } else {
        setRegisterError(result.message || 'Registration failed');
      }
    } catch (err) {
      setRegisterError('An unexpected error occurred');
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="landing">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              Swahili Audio & Video
              <span className="hero-accent">Transcription & Translation</span>
            </h1>
            <p className="hero-subtitle">
              Automatically transcribe Swahili audio and video files, then translate to English. Perfect for researchers, educators, and content creators working with East African languages.
            </p>
            <div className="hero-actions">
              <Button variant="primary" onClick={openRegisterModal}>
                Get Started
              </Button>
              <Button variant="secondary" onClick={openLoginModal}>
                Watch Demo
              </Button>
            </div>
          </div>
          <div className="hero-image-container">
            <img
              src="/images/hero-image.png"
              alt="NuruSubs Transcription Platform"
              className="hero-image"
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">
            Easily transcribe and translate Swahili content into English in just a few simple steps.
          </p>
          <div className="steps-grid">
            <Card className="step-card">
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                  <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"/>
                </svg>
              </div>
              <h3>Upload Your File</h3>
              <p>Upload Swahili audio (MP3, WAV, FLAC, M4A) or video (MP4, AVI, MOV, MKV) files.</p>
            </Card>

            <Card className="step-card">
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                  <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12 3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97 0-.33-.03-.66-.07-1l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.31-.61-.22l-2.49 1c-.52-.39-1.06-.73-1.69-.98l-.37-2.65c-.04-.24-.25-.42-.5-.42h-4c-.25 0-.46.18-.5.42l-.37 2.65c-.63.25-1.17.59-1.69.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64L4.57 11c-.04.34-.07.67-.07 1 0 .33.03.65.07.97l-2.11 1.66c-.19.15-.25.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1.01c.52.4 1.06.74 1.69.99l.37 2.65c.04.24.25.42.5.42h4c.25 0 .46-.18.5-.42l.37-2.65c.63-.26 1.17-.59 1.69-.99l2.49 1.01c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.66z"/>
                </svg>
              </div>
              <h3>Automatic Processing</h3>
              <p>Our AI-powered system transcribes your Swahili content with high accuracy.</p>
            </Card>

            <Card className="step-card">
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                  <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0 0 14.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z"/>
                </svg>
              </div>
              <h3>Instant Translation</h3>
              <p>Get English translations automatically generated from your Swahili transcript.</p>
            </Card>

            <Card className="step-card">
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zm-8 2V5h2v6h1.17L12 13.17 9.83 11H11zm-6 7h14v2H5v-2z"/>
                </svg>
              </div>
              <h3>Download Results</h3>
              <p>Download text files for audio or SRT subtitle files for video content.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">Key Features</h2>
          <div className="features-grid">
            <Card className="feature-card">
              <h3>⚡ Lightning Fast</h3>
              <p>Process hours of content in minutes and save time</p>
            </Card>

            <Card className="feature-card">
              <h3>🌍 English Translation</h3>
              <p>Automatic English translations from Swahili transcripts</p>
            </Card>

            <Card className="feature-card">
              <h3>🎬 Video Subtitles</h3>
              <p>Generate SRT subtitle files with accurate timestamps</p>
            </Card>

            <Card className="feature-card">
              <h3>💰 Cost-Effective</h3>
              <p>60 minutes of transcription monthly for researchers</p>
            </Card>

            <Card className="feature-card">
              <h3>🔒 Secure</h3>
              <p>End-to-end encryption with automatic file cleanup</p>
            </Card>

            <Card className="feature-card">
              <h3>📊 Track Progress</h3>
              <p>Real-time dashboard to monitor transcription jobs</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container text-center">
          <h2>Ready to Get Started?</h2>
          <p className="cta-text">
            Join researchers, educators, and content creators using NuruSubs to break language barriers.
          </p>
          <Button variant="primary" onClick={openRegisterModal}>
            Create Account
          </Button>
        </div>
      </section>

      {/* Login Modal */}
      <Modal
        isOpen={showLoginModal}
        onClose={closeModals}
        title="Welcome Back"
      >
        <form onSubmit={handleLogin} className="auth-form">
          {loginError && <div className="error-message">{loginError}</div>}

          <div className="form-group">
            <label htmlFor="login-email">Email</label>
            <input
              type="email"
              id="login-email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
              placeholder="your@email.com"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="login-password">Password</label>
            <input
              type="password"
              id="login-password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="form-input"
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            disabled={loginLoading}
            className="auth-button"
          >
            {loginLoading ? 'Signing in...' : 'Sign In'}
          </Button>

          <div className="modal-footer-text">
            Don't have an account?{' '}
            <button
              type="button"
              className="text-link"
              onClick={openRegisterModal}
            >
              Create account
            </button>
          </div>
        </form>
      </Modal>

      {/* Register Modal */}
      <Modal
        isOpen={showRegisterModal}
        onClose={closeModals}
        title="Create Account"
      >
        <form onSubmit={handleRegister} className="auth-form">
          {registerError && <div className="error-message">{registerError}</div>}
          {registerSuccess && <div className="success-message">{registerSuccess}</div>}

          <div className="form-group">
            <label htmlFor="register-email">Email</label>
            <input
              type="email"
              id="register-email"
              value={registerEmail}
              onChange={(e) => setRegisterEmail(e.target.value)}
              required
              placeholder="your@email.com"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="register-password">Password</label>
            <input
              type="password"
              id="register-password"
              value={registerPassword}
              onChange={(e) => setRegisterPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="form-input"
            />
            <PasswordStrength password={registerPassword} showValidation={true} />
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirm Password</label>
            <input
              type="password"
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="form-input"
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            disabled={registerLoading}
            className="auth-button"
          >
            {registerLoading ? 'Creating account...' : 'Create Account'}
          </Button>

          <div className="modal-footer-text">
            Already have an account?{' '}
            <button
              type="button"
              className="text-link"
              onClick={openLoginModal}
            >
              Sign in
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Landing;
